// Response and input schemas for the safeguards register, the evidence line page and the frozen
// task and evidence views (plan §4, spec F7 and §5.5; T26, T32 follow-up). Shapes follow
// apps/api/src/modules/safeguards/service.ts, evidence/view.ts and freeze/views.ts; unknown keys are
// dropped. Leads text and exception ids are already redacted by the API for team roles
// (constitution §6.4); the client only parses and never adds them back.
import { EVIDENCE_STATUSES, isIsoDate, today } from '@cis/shared';
import { z } from 'zod';
import { personSchema, taskStateSchema } from './tasks.ts';

// Text caps are the server's caps times the redaction headroom (see schemas/tasks.ts): for team
// roles a Leads id ("R1") becomes "[Leads only]" after the server applied its cap (review B3-R1).
const REDACTION_HEADROOM = 6;
const SERVER_LABEL_CAP = 500;
const SERVER_TEXT_CAP = 20_000;
export const EVIDENCE_URL_MAX = 2000;
export const EVIDENCE_NOTE_MAX = 500;
const MAX_ID = 64;
const MAX_LABEL = SERVER_LABEL_CAP * REDACTION_HEADROOM;
const MAX_TEXT = SERVER_TEXT_CAP * REDACTION_HEADROOM;
const MAX_URL = EVIDENCE_URL_MAX * REDACTION_HEADROOM;
const MAX_STAMP = 64;
const MAX_LIST = 1000;
const MAX_PAGE_ITEMS = 100;

/** Evidence links must live in the NewGen SharePoint tenant (spec §5.5, §8.11). */
export const EVIDENCE_URL_PREFIX = 'https://newgenhcg.sharepoint.com/';
const EVIDENCE_HOST = 'newgenhcg.sharepoint.com';

const text = z.string().max(MAX_TEXT);
const nullableText = text.nullable();
const stamp = z.string().max(MAX_STAMP);
const label = z.string().max(MAX_LABEL);

export const evidenceStatusSchema = z.enum(EVIDENCE_STATUSES);
export const SAFEGUARD_STATUSES = ['none', ...EVIDENCE_STATUSES] as const;
export const safeguardStatusSchema = z.enum(SAFEGUARD_STATUSES);

// GET /safeguards: one row per safeguard with the status of its evidence.
export const safeguardSummarySchema = z.object({
  code: z.string().min(1).max(MAX_ID),
  title: label,
  family: z.object({ code: z.string().max(MAX_ID), title: label }),
  ig: z.number().int(),
  assetType: label.nullable(),
  securityFunction: label.nullable(),
  inScope: z.boolean(),
  retired: z.boolean(),
  status: safeguardStatusSchema,
  lineCount: z.number().int().nonnegative(),
});

export const safeguardPageSchema = z.object({
  items: z.array(safeguardSummarySchema).max(MAX_PAGE_ITEMS),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});

// GET /evidence/:code: the line, its state and its records.
export const evidencePersonSchema = z.object({
  id: z.number().int().positive(),
  name: label,
});

export const evidenceRecordSchema = z.object({
  id: z.number().int().positive(),
  url: z.string().max(MAX_URL),
  sha256: z.string().max(MAX_ID).nullable(),
  capturedOn: stamp,
  note: nullableText,
  addedBy: evidencePersonSchema,
  addedAt: stamp,
  checkedBy: evidencePersonSchema.nullable(),
  checkedAt: stamp.nullable(),
});

export const evidenceLineSchema = z.object({
  code: z.string().min(1).max(MAX_ID),
  title: label,
  safeguard: z.object({ code: z.string().max(MAX_ID), title: label }),
  retired: z.boolean(),
  content: z.unknown(),
  state: z.object({
    status: evidenceStatusSchema,
    exceptionId: label.nullable(),
    version: z.number().int().nonnegative(),
  }),
  records: z.array(evidenceRecordSchema).max(MAX_LIST),
  asOf: stamp.nullable(),
});

// The pack's text for a line (artifact, cadence, bundle...). Anything missing or of another
// shape is simply not shown.
const shownText = z.string().max(MAX_TEXT).catch('');
export const evidenceLineContentSchema = z
  .object({
    artifact: shownText,
    cadence: shownText,
    bundle: shownText,
    plan: shownText,
    firstRecord: shownText,
    tasks: z.array(z.string().max(MAX_ID)).max(MAX_LIST).catch([]),
  })
  .catch({ artifact: '', cadence: '', bundle: '', plan: '', firstRecord: '', tasks: [] });

// Frozen views (GET /freezes/:id/evidence/:code and /freezes/:id/tasks/:taskId). The freeze label
// is redacted for team roles, so it gets the same headroom as other labels.
const frozenRefSchema = z.object({
  id: z.number().int().positive(),
  label,
  at: stamp,
});

export const frozenEvidenceSchema = z.object({
  freeze: frozenRefSchema,
  afterFreeze: z.boolean(),
  line: evidenceLineSchema,
});

const people = z.array(personSchema).max(MAX_LIST);

export const frozenStepSchema = z.object({
  no: z.number().int().positive(),
  do: text,
  how: nullableText,
  check: nullableText,
  who: nullableText,
  required: z.boolean(),
  assignees: people,
  tick: z.object({ by: personSchema, at: stamp, textChanged: z.boolean() }).nullable(),
});

export const frozenTaskSchema = z.object({
  freeze: frozenRefSchema,
  afterFreeze: z.boolean(),
  taskId: z.string().min(1).max(MAX_ID),
  title: label,
  phase: z.object({ code: z.string().max(MAX_ID), title: label.nullable().optional() }),
  area: label,
  lane: z.string().max(MAX_ID),
  tier: z.string().max(MAX_ID),
  plannedStart: stamp.nullable(),
  plannedEnd: stamp.nullable(),
  retired: z.boolean(),
  content: z.unknown(),
  state: taskStateSchema,
  assignees: people,
  steps: z.array(frozenStepSchema).max(MAX_LIST),
});

// The add-record form (spec §5.5): the same rules the API applies, so a person sees every problem
// next to its field before anything is sent. Values are the form's strings.

// No whitespace or control characters anywhere in a link.
// Matches "https://newgenhcg.sharepoint.com/sites/x"; rejects "https://x/a b", "https://x/\t".
// https://regex101.com/?flavor=javascript&regex=%5E%5B%5E%5Cs%5Cx00-%5Cx1f%5Cx7f%5D%2B%24&testString=https%3A%2F%2Fnewgenhcg.sharepoint.com%2Fsites%2Fx
// eslint-disable-next-line no-control-regex -- control characters are exactly what this refuses
const NO_SPACE_OR_CONTROL = /^[^\s\x00-\x1f\x7f]+$/;

// SHA-256 as 64 lowercase hex digits; uppercase is refused, not folded (the API does the same).
// Matches "e3b0c442...b855" (64 chars); rejects uppercase hex and 63 or 65 characters.
// https://regex101.com/?flavor=javascript&regex=%5E%5B0-9a-f%5D%7B64%7D%24&testString=e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855
export const SHA256_HEX = /^[0-9a-f]{64}$/;

/** True for a link the API accepts: https, the NewGen tenant host, no user info, no spaces. */
export function isEvidenceUrl(url: string): boolean {
  if (!url.startsWith(EVIDENCE_URL_PREFIX) || !NO_SPACE_OR_CONTROL.test(url)) return false;
  try {
    const parsed = new URL(url);
    return parsed.host === EVIDENCE_HOST && parsed.username === '' && parsed.password === '';
  } catch {
    return false;
  }
}

function shaProblem(value: string): string | null {
  if (value === '' || SHA256_HEX.test(value)) return null;
  if (SHA256_HEX.test(value.toLowerCase())) return 'Use lowercase letters a to f in the SHA-256.';
  return 'A SHA-256 is 64 characters: the digits 0 to 9 and the letters a to f.';
}

export const evidenceRecordFormSchema = z.object({
  url: z
    .string()
    .trim()
    .min(1, 'Paste the SharePoint link to the evidence.')
    .max(EVIDENCE_URL_MAX, `Keep the link under ${String(EVIDENCE_URL_MAX)} characters.`)
    .refine(isEvidenceUrl, `The link must start with ${EVIDENCE_URL_PREFIX}`),
  sha256: z
    .string()
    .trim()
    .superRefine((v, ctx) => {
      const problem = shaProblem(v);
      if (problem !== null) ctx.addIssue({ code: 'custom', message: problem });
    }),
  capturedOn: z
    .string()
    .refine(isIsoDate, 'Choose the date the evidence was captured.')
    .refine((d) => d <= today(), 'The capture date cannot be in the future.'),
  note: z
    .string()
    .trim()
    .max(EVIDENCE_NOTE_MAX, `Keep the note under ${String(EVIDENCE_NOTE_MAX)} characters.`),
});

export type SafeguardStatus = z.infer<typeof safeguardStatusSchema>;
export type SafeguardSummary = z.infer<typeof safeguardSummarySchema>;
export type SafeguardPage = z.infer<typeof safeguardPageSchema>;
export type EvidencePerson = z.infer<typeof evidencePersonSchema>;
export type EvidenceRecord = z.infer<typeof evidenceRecordSchema>;
export type EvidenceLine = z.infer<typeof evidenceLineSchema>;
export type EvidenceLineContent = z.infer<typeof evidenceLineContentSchema>;
export type FrozenEvidence = z.infer<typeof frozenEvidenceSchema>;
export type FrozenStep = z.infer<typeof frozenStepSchema>;
export type FrozenTask = z.infer<typeof frozenTaskSchema>;
export type EvidenceRecordFormInput = z.input<typeof evidenceRecordFormSchema>;
export type EvidenceRecordForm = z.output<typeof evidenceRecordFormSchema>;
